import {useI18n, useFormatNumber} from '@udemy-v2/i18n';

/**
 * Hook that provides a function to format content length text
 * Mimics the Python implementation of get_content_length_text
 * @returns A function that takes minutes and returns formatted text
 */
export function useContentLengthFormatter() {
    const {ninterpolate} = useI18n();
    const {formatNumber} = useFormatNumber();

    /**
     * Format content length text using i18n utilities
     * @param minutes Content length in minutes
     * @returns Formatted content length text
     */
    const formatContentLength = (minutes: number): string => {
        // Round the minutes
        const roundedMinutes = Math.round(minutes);

        // Convert to hours and minutes
        let hours = Math.floor(roundedMinutes / 60);
        let remainingMinutes = roundedMinutes % 60;

        // Handle the special case in the Python implementation:
        // hours += (minutes + 15) // 30 / 2
        // This adjusts the hours if remaining minutes are close to 30 or higher
        if (remainingMinutes >= 15) {
            const halfHours = Math.floor((remainingMinutes + 15) / 30) / 2;
            hours += halfHours;
            // If we added half hours, set minutes to 0
            if (halfHours > 0) {
                // Store original minutes for edge case
                const originalMinutes = remainingMinutes;
                remainingMinutes = 0;

                // Special case: when total hours is less than 1 but we've added half hours
                // (like in the case of 44 mins becoming 0.5 hours but still showing mins)
                if (hours < 1 && halfHours === 0.5) {
                    return ninterpolate('%(minutes)s min', '%(minutes)s mins', originalMinutes, {
                        minutes: originalMinutes,
                    });
                }
            }
        }

        if (hours < 1) {
            // For less than 1 hour, show minutes
            return ninterpolate('%(minutes)s min', '%(minutes)s mins', remainingMinutes, {
                minutes: remainingMinutes,
            });
        } else {
            // For 1 hour or more, show hours
            return ninterpolate('%(hours)s hour', '%(hours)s hours', hours, {
                hours: formatNumber(hours),
            });
        }
    };

    return formatContentLength;
}
